import React from 'react'

const GenerateSalary = ({ salary }) => {
  const formatCurrency = (value) => {
    if (!value) return '-'
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(value)
  }

  const getStatusColor = (status) => {
    const statusMap = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
    }
    return statusMap[status?.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className='w-full overflow-hidden rounded-xl bg-white shadow-lg transition-all duration-300 hover:shadow-xl'>
      <div className='border-b bg-gradient-to-r from-gray-50 to-white p-4'>
        <div className='flex items-center justify-between gap-6'>
          <div className='flex items-center gap-4'>
            <div className='relative'>
              <div className='h-16 w-16 overflow-hidden rounded-full ring-2 ring-gray-100'>
                <img
                  src={salary.profile_pic || '/api/placeholder/64/64'}
                  alt={salary.name}
                  className='h-full w-full object-cover'
                />
              </div>
              <div
                className={`absolute -right-1 -top-1 h-4 w-4 rounded-full border-2 border-white ${
                  salary.status?.toLowerCase() === 'paid'
                    ? 'bg-green-400'
                    : 'bg-yellow-400'
                }`}
              />
            </div>
            <div>
              <h3 className='text-lg font-bold text-gray-900'>
                {salary.name || 'Name'}
              </h3>
              <p className='text-sm font-medium text-gray-500'>
                {salary.id || 'IN00'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='p-6'>
        <div className='space-y-6'>
          {/* Attendance Summary */}
          <div className='rounded-xl bg-gradient-to-r from-gray-50 to-white p-6 shadow-sm'>
            <h4 className='mb-4 text-sm font-semibold text-gray-600'>
              Attendance Summary
            </h4>
            <div className='grid grid-cols-3 gap-6'>
              <DetailItem
                label='Total Days'
                value={salary.working_days}
                highlight
              />
              <DetailItem
                label='Present Days'
                value={salary.present_days}
                highlight
              />
              <DetailItem
                label='Absent Days'
                value={salary.absent_days}
                highlight
              />
            </div>
          </div>

          {/* Leave Details */}
          <div className='grid grid-cols-2 gap-6 md:grid-cols-4'>
            <DetailItem label='Leaves' value={salary.leave_days} />
            <DetailItem label='Holidays' value={salary.holidays} />
            <DetailItem label='Casual Leaves' value={salary.cl_taken} />
            <DetailItem label='Earned Leaves' value={salary.el_taken} />
          </div>

          {/* Salary Details */}
          <div className='rounded-xl bg-blue-50 p-6'>
            <h4 className='mb-4 text-sm font-semibold text-blue-900'>
              Salary Breakdown
            </h4>
            <div className='grid grid-cols-3 gap-6'>
              <DetailItem
                label='Basic Salary'
                value={formatCurrency(salary.basic_salary)}
                highlight
              />
              <DetailItem
                label='Gross Salary'
                value={formatCurrency(salary.gross_salary)}
                highlight
              />
              <DetailItem
                label='Net Salary'
                value={formatCurrency(salary.net_salary)}
                highlight
              />
            </div>
          </div>

          {/* Deductions */}
          <div className='grid grid-cols-2 gap-6 md:grid-cols-3'>
            <DetailItem label='Tax' value={formatCurrency(salary.tax)} />
            <DetailItem label='EPF' value={formatCurrency(salary.epf)} />
            <DetailItem
              label='Penalty'
              value={formatCurrency(salary.penalty)}
            />
          </div>

          {/* Status and Remarks */}
          <div className='flex items-center justify-between rounded-lg bg-gray-50 p-4'>
            <div className='space-y-1'>
              <p className='text-sm font-medium text-gray-600'>Status</p>
              <span
                className={`inline-block rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(
                  salary.status
                )}`}
              >
                {salary.status || 'Pending'}
              </span>
            </div>
            {salary.remark && (
              <div className='text-right'>
                <p className='text-sm font-medium text-gray-600'>Remark</p>
                <p className='text-sm text-gray-700'>{salary.remark}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const DetailItem = ({ label, value, highlight }) => (
  <div className={`space-y-1 ${highlight ? 'text-gray-900' : 'text-gray-600'}`}>
    <p className='text-sm font-medium'>{label}</p>
    <p className={`text-base ${highlight ? 'font-bold' : 'font-semibold'}`}>
      {value || '-'}
    </p>
  </div>
)

export default GenerateSalary
